import './App.css';
import { Phone, MapPin, Mail, Scissors, Home, Sparkles, GraduationCap, Facebook, ArrowUpRight, Menu, X } from "lucide-react";
import { useState, useRef, useEffect } from 'react';
import { ScrollTop } from 'primereact/scrolltop';
import { motion, AnimatePresence } from 'framer-motion';
import { Analytics } from "@vercel/analytics/react";
import 'non.geist';
import { div } from 'framer-motion/client';


function importAll(r) {
  return r.keys().map(r);
}

const images = importAll(require.context('../public/gallery', false, /\.(png|jpe?g|svg)$/));

const navItems = [
  { label: 'Domů', id: 'domu' },
  { label: 'O mně', id: 'o-mne' },
  { label: 'Galerie', id: 'galerie' },
  { label: 'Ceník', id: 'cenik' },
  { label: 'Kontakt', id: 'kontakt' },
];

const services = [
  { name: "Dámský střih – začištění konečků", price: "100,-" },
  { name: "Dámský střih – sestříhávání, sestřih do tvaru atd.", price: "150,-" },
  { name: "Dámský střih – mix technik, složitější stříhání", price: "200,-" },
  { name: "Pánský střih", price: "200,-" },
  { name: "Mytí a regenerace *", price: "90,-/140,-/190,-" },
  { name: "Maska, regenerační zábal *", price: "50,-/ 100,-/ 150,-" },
  { name: "Tónování, přeliv *", price: "280,-/470,-/740,-" },
  { name: "Odbarvení *", price: "320,-/480,-/700,-" },
  { name: "Techniky do fólie – melír, baleyage atd. *", price: "520,-/730,-/940,-" },
  { name: "Barva permanentní *", price: "300,-/500,-/800,-" },
  { name: "Foukaná přes kartáč, žehlení, kulmování atd.", price: "120,-/170,-/220,-" },
  { name: "Finální úprava – styling: olej, tužidlo, gel, lak atd. *", price: "20,-" },
];

const quickInfo = [
  { icon: Home, text: 'Třebíč, kraj Vysočina' },
  { icon: Sparkles, text: '10+ let praxe' },
  { icon: GraduationCap, text: 'Obchodní akademie a Hotelová škola Třebíč' },
];

const contactInfo = [
  { icon: Phone, title: 'Telefon', content: '+420 606 233 992' },
  { icon: MapPin, title: 'Adresa', content: 'Bráfova tř. 525/41, Třebíč 674 01' },
  { icon: Mail, title: 'E-mail', content: 'alice.homolkova@post.cz' },
  { icon: Scissors, title: 'Objednání předem', content: ['Pracuji liché týdny v odpoledních hodinách.'] },
];

const slogans = [
  ",,Vlasy - moje láska\"",
  ",,Práce jako hobby\"",
  ",,Citlivě k přání zákazníka\"",
  ",,Kreativita na počkání\"",
];

// Popisek číslované sekce ve stylu „01 — Nadpis"
function SectionHeading({ number, title }) {
  return (
    <motion.div
      className="mb-12"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5 }}
    >
      <span className="text-sm font-medium tracking-[0.2em] uppercase text-pink-400">
        {number} — {title}
      </span>
      <h2 className="mt-3 text-4xl sm:text-5xl font-bold text-gray-900">{title}</h2>
    </motion.div>
  );
}

function App() {
  const [showAll, setShowAll] = useState(false);
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const [currentSloganIndex, setCurrentSloganIndex] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  const galleryRef = useRef(null);

  // Pokud je showAll true, zobrazí se všechny obrázky, jinak jen 6
  const visibleImages = showAll ? images : images.slice(0, 6);

  const handleToggleShowAll = () => {
    setShowAll(!showAll);
    // Při skrytí posune pohled zpět na začátek galerie
    if (showAll && galleryRef.current) {
      galleryRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Střídání sloganů každých 5 sekund
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentSloganIndex((prevIndex) => (prevIndex + 1) % slogans.length);
    }, 5000);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <div>
      <div className="App bg-[#fff7fa] text-gray-900 z-50">
        {/* Navigace */}
        <nav className="fixed top-0 inset-x-0 z-50 bg-[#fff7fa]/80 backdrop-blur-md border-b border-pink-100">
          <div className="max-w-6xl mx-auto px-5">
            <div className="flex justify-between items-center h-16">
              <a href="#domu" className="flex items-center gap-3" onClick={() => setMenuOpen(false)}>
                <img
                  className="h-9 w-9 rounded-full object-cover ring-2 ring-pink-200"
                  src="logo.jpg"
                  alt="Logo"
                  width={36}
                  height={36}
                />
                <span className="text-lg font-semibold tracking-tight">Alice Homolková</span>
              </a>

              {/* Desktop odkazy */}
              <div className="hidden md:flex items-center gap-1">
                {navItems.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className="text-sm text-gray-600 hover:text-pink-600 px-3 py-2 rounded-full transition-colors duration-150"
                  >
                    {item.label}
                  </a>
                ))}
                <a
                  href="#kontakt"
                  className="ml-2 inline-flex items-center gap-1 text-sm font-medium bg-pink-500 hover:bg-pink-600 text-white px-4 py-2 rounded-full transition-colors duration-200"
                >
                  Objednat se
                </a>
              </div>

              {/* Mobilní přepínač */}
              <button
                className="md:hidden p-2 -mr-2 text-gray-700"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Menu"
              >
                {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Mobilní menu */}
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                className="md:hidden border-t border-pink-100 bg-[#fff7fa]"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="px-5 py-3 flex flex-col">
                  {navItems.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      onClick={() => setMenuOpen(false)}
                      className="py-3 text-gray-700 hover:text-pink-600 border-b border-pink-50 last:border-0"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>

        {/* Hero / Domů */}
        <section id="domu" className="relative overflow-hidden pt-16">
          <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-pink-200/50 blur-3xl" />
          <div className="absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-pink-300/30 blur-3xl" />

          <div className="relative max-w-6xl mx-auto px-5 min-h-[calc(100vh-4rem)] grid md:grid-cols-2 gap-12 items-center py-16">
            <motion.div
              className="text-left"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 text-sm font-medium tracking-[0.2em] uppercase text-pink-500">
                <Scissors className="h-4 w-4" /> Kadeřnice v Třebíči
              </span>
              <h1 className="mt-5 text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05]">
                Alice<br />Homolková
              </h1>

              <div className="mt-6 h-8">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={currentSloganIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.4 }}
                    className="text-xl text-pink-600 italic"
                  >
                    {slogans[currentSloganIndex]}
                  </motion.p>
                </AnimatePresence>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#kontakt"
                  className="inline-flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white font-medium px-6 py-3 rounded-full transition-colors duration-200 shadow-lg shadow-pink-500/20"
                >
                  Objednat se <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="#galerie"
                  className="inline-flex items-center gap-2 bg-white hover:bg-pink-50 text-gray-800 font-medium px-6 py-3 rounded-full transition-colors duration-200 ring-1 ring-pink-100"
                >
                  Prohlédnout práci
                </a>
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <div className="absolute inset-0 -rotate-6 rounded-[2rem] bg-pink-200/60" />
              <img
                className="relative w-full max-w-md mx-auto aspect-[4/5] object-cover rounded-[2rem] shadow-2xl"
                src="self.jpg"
                alt="Alice Homolková"
              />
            </motion.div>
          </div>
        </section>

        {/* 01 — O mně */}
        <section id="o-mne" className="py-24 bg-white">
          <div className="max-w-6xl mx-auto px-5">
            <SectionHeading number="01" title="O mně" />
            <div className="grid md:grid-cols-2 gap-10">
              <motion.div
                className="space-y-5 text-lg text-gray-600 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5 }}
              >
                <p>
                  Začalo to pletením copánků v dětství. Pokračovalo to natáčením kudrlinek v pubertě. A skončilo to rozhodnutím žít svůj sen v dospělosti.
                  Jsem vyučená kadeřnice působící v Třebíči, v Kadeřnictví EVA.
                </p>
                <p>
                  Nabízím klasické kadeřnické služby s kreativním, ale především empatickým a pečlivým přístupem.
                  Na mé stránce můžete najít fotografie mých prací, ceník mých služeb a kontakt k objednání předem.
                </p>
              </motion.div>

              <motion.div
                className="flex flex-col gap-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                {quickInfo.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 bg-pink-50 rounded-2xl px-5 py-4">
                    <div className="bg-white rounded-xl p-3 shadow-sm shrink-0">
                      <item.icon className="w-5 h-5 text-pink-500" />
                    </div>
                    <span className="text-gray-700">{item.text}</span>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* 02 — Galerie */}
        <section id="galerie" className="py-24 bg-[#fff7fa]" ref={galleryRef}>
          <div className="max-w-6xl mx-auto px-5">
            <SectionHeading number="02" title="Galerie" />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
              {visibleImages.map((image, index) => (
                <motion.div
                  key={index}
                  className="group aspect-square relative overflow-hidden rounded-2xl shadow-sm hover:shadow-xl transition-shadow duration-300"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src={image}
                    alt={`Galerie obrázek ${index + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-pink-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-12">
              <button
                onClick={handleToggleShowAll}
                className="inline-flex items-center gap-2 bg-white hover:bg-pink-50 text-gray-800 font-medium py-3 px-8 rounded-full transition-colors duration-300 ring-1 ring-pink-200 shadow-sm"
              >
                {showAll ? 'Zobrazit méně' : 'Zobrazit více'}
              </button>
            </div>
          </div>
        </section>

        {/* 03 — Ceník */}
        <section id="cenik" className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-5">
            <SectionHeading number="03" title="Ceník" />
            <motion.div
              className="bg-[#fff7fa] rounded-3xl p-6 sm:p-10 ring-1 ring-pink-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center justify-between pb-4 mb-2 border-b border-pink-200 text-sm font-medium tracking-wide uppercase text-pink-400">
                <span>Služba</span>
                <span>Cena (Kč)</span>
              </div>
              <div className="divide-y divide-pink-100">
                {services.map((service, index) => (
                  <div key={index} className="flex items-baseline justify-between gap-6 py-4">
                    <span className="text-gray-700">{service.name}</span>
                    <span className="font-semibold text-pink-600 whitespace-nowrap">{service.price}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-sm text-gray-400">* dle spotřeby materiálu</p>
            </motion.div>
          </div>
        </section>

        {/* 04 — Kontakt */}
        <section id="kontakt" className="py-24 bg-[#fff7fa]">
          <div className="max-w-6xl mx-auto px-5">
            <SectionHeading number="04" title="Kontakt" />
            <div className="grid md:grid-cols-2 gap-8 items-start">
              <motion.div
                className={`aspect-square relative overflow-hidden rounded-3xl shadow-lg transition-all duration-300 ease-in-out ${isMapExpanded ? 'md:col-span-2' : ''}`}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <iframe
                  title="Mapa – Bráfova tř. 525/41, Třebíč"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2606.374263284188!2d15.883560476861973!3d49.21242887572612!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x470d42360ce1f9b9%3A0x65a9746b873acd9d!2zQnLDoWZvdmEgdMWZLiA1MjUvNDEsIDY3NCAwMSBUxZllYsOtxI0gMQ!5e0!3m2!1scs!2scz!4v1725563535889!5m2!1scs!2scz"
                  className="w-full h-full border-0"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <button
                  onClick={() => setIsMapExpanded(!isMapExpanded)}
                  className="absolute bottom-4 right-4 bg-pink-500 text-white px-4 py-2 rounded-full shadow-md hover:bg-pink-600 transition-colors duration-300"
                >
                  {isMapExpanded ? 'Zmenšit mapu' : 'Zvětšit mapu'}
                </button>
              </motion.div>

              <div className="grid gap-4">
                {contactInfo.map((item, index) => (
                  <motion.div
                    key={index}
                    className="bg-white rounded-2xl ring-1 ring-pink-100 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                  >
                    <div className="p-6 flex items-start gap-4">
                      <div className="bg-pink-100 rounded-xl p-3 shrink-0">
                        <item.icon className="h-6 w-6 text-pink-500" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-gray-900 text-lg mb-1">{item.title}</h3>
                        {Array.isArray(item.content) ? (
                          item.content.map((line, i) => (
                            <p key={i} className="text-gray-600">{line}</p>
                          ))
                        ) : (
                          <p className="text-gray-600">{item.content}</p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}

                <motion.div
                  className="bg-white rounded-2xl ring-1 ring-pink-100 shadow-sm p-6 flex items-center justify-between"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: contactInfo.length * 0.08 }}
                >
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900 text-lg">Sledujte mě</h3>
                    <p className="text-gray-600">Aktuální práce na Facebooku</p>
                  </div>
                  <a
                    href="https://www.facebook.com/Sluzby.Sakura33"
                    aria-label="Facebook"
                    className="bg-pink-100 hover:bg-pink-500 text-pink-500 hover:text-white p-3 rounded-xl transition-colors duration-300"
                  >
                    <Facebook className="h-6 w-6" />
                  </a>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Patička */}
        <footer className="w-full py-8 bg-white border-t border-pink-100">
          <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <div className="relative w-10 h-10 bg-black rounded-full overflow-hidden">
                <img
                  src="rp-logo.jpg"
                  alt="Radim Pokorný Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-900">vyvinutý od</span>
                <span className="text-lg font-semibold text-gray-800">Radim Pokorný</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">Designer &amp; Developer</span>
              <a
                href="https://radim-pokorny.cz/"
                className="group relative px-4 py-2 text-sm font-medium text-gray-700 transition-colors duration-300 ease-in-out hover:text-gray-900"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="relative z-10">Navštívit</span>
                <span className="absolute inset-0 bg-pink-50 rounded transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-in-out origin-left"></span>
              </a>
            </div>
          </div>
        </footer>

        <ScrollTop className="bg-pink-50 p-3 rounded-full shadow-md" />
        <Analytics />
      </div>
    </div>
  );
}

export default App;
